import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers'
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import {
  type ColumnDef,
  type ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  type Row,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import React, { useState } from 'react'
import { DraggableColumnHeader } from './DraggableColumnHeader'
import { DraggableTableCell } from './DraggableTableCell'

interface EnhancedTableProps<T> {
  data: T[]
  columns: ColumnDef<T>[]
  getRowCanExpand?: (row: Row<T>) => boolean
  renderSubComponent?: (row: Row<T>) => React.ReactNode
  className?: string
  height?: string
  onRowClick?: (row: T) => void
}

export function EnhancedTable<T>({
  data,
  columns,
  getRowCanExpand = () => false,
  renderSubComponent,
  className = '',
  height = '10vh',
  onRowClick,
}: EnhancedTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [expanded, setExpanded] = useState<ExpandedState>({})
  const [columnOrder, setColumnOrder] = useState<string[]>([])

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      expanded,
      columnOrder,
    },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    onColumnOrderChange: setColumnOrder,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand,
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
  })

  // Initialize column order
  React.useEffect(() => {
    if (columnOrder.length === 0) {
      setColumnOrder(table.getAllColumns().map(col => col.id))
    }
  }, [table, columnOrder.length])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (active && over && active.id !== over.id) {
      const oldIndex = columnOrder.indexOf(active.id as string)
      const newIndex = columnOrder.indexOf(over.id as string)
      setColumnOrder(arrayMove(columnOrder, oldIndex, newIndex))
    }
  }

  return (
    <div className={`w-full ${className}`}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToHorizontalAxis]}
      >
        <div className='overflow-hidden border rounded-lg'>
          <div className='overflow-auto' style={{ height }}>
            <table className='w-full border-collapse'>
              <thead className='bg-slate-100 dark:bg-slate-800 sticky top-0 z-20'>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    <SortableContext
                      items={columnOrder}
                      strategy={horizontalListSortingStrategy}
                    >
                      {headerGroup.headers.map(header => (
                        <DraggableColumnHeader
                          key={header.id}
                          header={header}
                          table={table}
                        />
                      ))}
                    </SortableContext>
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => (
                  <React.Fragment key={row.id}>
                    <tr
                      className='border-b hover:bg-slate-50 dark:hover:bg-slate-800 h-14 cursor-pointer'
                      onClick={() => onRowClick?.(row.original)}
                    >
                      <SortableContext
                        items={columnOrder}
                        strategy={horizontalListSortingStrategy}
                      >
                        {row.getVisibleCells().map(cell => (
                          <DraggableTableCell key={cell.id} cell={cell} />
                        ))}
                      </SortableContext>
                    </tr>
                    {row.getIsExpanded() && renderSubComponent && (
                      <tr>
                        <td colSpan={row.getVisibleCells().length}>
                          {renderSubComponent(row)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </DndContext>
    </div>
  )
}
